import type { PromisePoolAllSettledResult } from '@/utils/promise.util';
import type { Pagination } from '@factoryfixinc/ats-interfaces/dist/payloads/ats-project/search-projects.type';
import type { CandidateOrigin } from '@/core/conversations/conversation-index/candidate-origin.enum';

export interface ConversationIndexHeaders {
  headers: ConversationIndex[];
}

export interface ApplicationIndex {
  applicationId: number;
  conversationIndexId: number;
  jobId: number;
  employerId: number;
  proId: number;
  applicationStatus: string | null;
  applicationApplyTs: string | null;
  score?: number;
  jobStatus: string;
  createTs: string;
  updateTs: string;
  jobDisplayTitle?: string;
  locationString?: string;
  initiatedByCopilot: boolean;
  initiatedByOutreach: boolean;
  integrationId?: string | null; // "<source>:<externalId>" when the applicant came from an ATS integration
  hasViewedRecord?: boolean; // Whether a watcher record exists in backend
  isViewed?: boolean; // The actual viewed status from backend (if record exists)
}

export interface ApplicationIndexWithJobApplicantSharedFields {
  jobId: number;
  score?: number;
}

export interface ConversationIndex {
  doNotContact?: boolean;
  conversationId: number;
  lastMessageText: string;
  lastMessageSentTs: string;
  lastProJobTitle?: string;
  lastProEmployerName?: string;
  proId: number;
  proName: string;
  proEmail: string;
  proPhoneNumber: string;
  isWatching?: boolean;
  readStatus: boolean;
  updateTs: string;
  applicationIndexes?: ApplicationIndex[];
  primaryApplicationIndex: Partial<ApplicationIndex>;
  isBulkSelection?: boolean;
  jobs?: import('@factoryfixinc/ats-interfaces').Job[];
}

/**
 * Result of advancing the selected conversation when it is no longer in the local list
 * (e.g. after a status change removed it from the active filter).
 */
export interface AdvanceSelectedConversationResult {
  /** True when the selection was changed because it was no longer in the list. */
  selectionAdvanced: boolean;
  /** The selection after the advance — null when no conversation could be selected. */
  nextSelectedConversation: ConversationIndex | null;
}

export enum SearchConversationOrderByFields {
  LAST_MESSAGE_SENT_TS = 'lastMessageSentTs',
  SCORE = 'score',
  APPLICATION_APPLY_TS = 'applicationApplyTs',
}

export type OrderByDirection = 'asc' | 'desc';

export interface SearchConversationOrderBy {
  field: SearchConversationOrderByFields;
  direction: OrderByDirection;
}

/** The settled outcome of the newest conversation list load. */
export interface ConversationIndexListFetchResult {
  requestId: number;
  succeeded: boolean;
  rowCount: number;
  /** False for a route handler's deep-link load, which inserts its own target afterwards. */
  autoSelectedFirst: boolean;
}

export interface SearchConversationIndex {
  onlyCurrentUser: boolean;
  onlyUnread: boolean;
  jobIds: number[];
  conversationIds: number[];
  applicationStatus: string[];
  origins: CandidateOrigin[];
  pagination: Pagination;
  orderBy: SearchConversationOrderBy;
  userProfileIds?: number[];
}

export type FetchConversationIndexesInput = {
  employerId: number;
  params: Partial<SearchConversationIndex>;
};

/**
 * One selectable application context in the account-wide candidate search
 * (ONE-6912). A candidate with several applications produces several rows.
 */
export interface GlobalSearchApplicationRow {
  conversationIndex: ConversationIndex;
  application: ApplicationIndex;
}

/**
 * A candidate grouped with all of their application rows outside the
 * selected job ("Across all jobs" zone).
 */
export interface GlobalSearchCandidateGroup {
  conversationIndex: ConversationIndex;
  rows: GlobalSearchApplicationRow[];
  /**
   * The candidate's cross-job application count before the row cap, so a
   * truncated group can say "2 of 5 applications" instead of misreporting
   * the truncated length as the candidate's total.
   */
  totalRows: number;
}

/**
 * Account-wide search results partitioned into the selected job (every
 * status/stage) and every other job, with the cross-job rows capped.
 */
export interface GlobalSearchPartition {
  /** Applications in the selected job, across every status/stage. */
  currentJobRows: GlobalSearchApplicationRow[];
  /** Applications in other jobs, grouped by candidate, capped at the row cap. */
  otherJobGroups: GlobalSearchCandidateGroup[];
  /** Cross-job rows rendered after the cap. */
  otherJobsShownCount: number;
  /**
   * True when cross-job rows were hidden by the cap. Only a boolean — the
   * endpoint caps candidates at 100 and counts candidates (not application
   * rows), so an exact hidden-row count cannot be derived honestly.
   */
  hasHiddenRows: boolean;
}

export type FetchConversationIndexByApplicantIdInput = {
  employerId: number;
  applicationId: number;
};

export enum ApplicationBulkActions {
  MOVE_TO_REVIEWED,
  MOVE_TO_REJECTED,
  MOVE_TO_REJECTED_W_REASON,
  EXPORT_CANDIDATES,
  ADD_TO_ANOTHER_JOB,
  BULK_MESSAGE,
}

export interface BulkChangeApplicationStatusResult {
  result: PromisePoolAllSettledResult<void>[];
  successCount: number;
  errorCount: number;
}
